import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ArrowIcon from './ArrowIcon';
import { SECTORS } from '../data/sectorServicesData';

function hexToRgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSectorsOpen, setMobileSectorsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const closeTimer = useRef(null);
  const location = useLocation();

  const isHome = location.pathname === '/';
  const isAbout = location.pathname === '/about';
  const isSectors = location.pathname === '/sectors' || location.pathname.startsWith('/industries/');
  const isBlog = location.pathname.startsWith('/blog');
  const isProjects = location.pathname.startsWith('/projects');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* Close mobile menu on route change */
  useEffect(() => { setMobileOpen(false); setMobileSectorsOpen(false); }, [location.pathname]);

  const show = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(false), 160); };
  const closeMobile = () => { setMobileOpen(false); setMobileSectorsOpen(false); };

  /* Scroll to top when clicking a link that's already the current page */
  const handleSamePageClick = (to) => {
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    closeMobile();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        width: 'min(1020px, 92vw)',
      }}
    >
      {/* Pill nav bar */}
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: isMobile ? '5px 8px 5px 14px' : '6px 10px 6px 18px',
          background: scrolled ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          border: '1px solid rgba(10,10,20,0.07)',
          borderRadius: 999,
          boxShadow: scrolled
            ? '0 16px 40px rgba(10,10,20,0.09)'
            : '0 8px 24px rgba(10,10,20,0.05)',
          transition: 'all 300ms ease',
        }}
      >
        <Link
          to="/"
          className="hoverable"
          style={{ display: 'flex', alignItems: 'center', marginRight: 'auto' }}
          onClick={() => handleSamePageClick('/')}
        >
          <img src="/zonexlogo.png" alt="Zone-X" style={{ height: 26, width: 'auto', display: 'block' }} />
        </Link>

        {/* Desktop links */}
        {!isMobile && (
          <>
            <NavLink to="/" label="Home" active={isHome} onClick={() => handleSamePageClick('/')} />
            <NavLink to="/about" label="About" active={isAbout} onClick={() => handleSamePageClick('/about')} />

            {/* Sectors trigger — every service lives under its sector, so there is
                a single merged trigger instead of separate Services/Sectors menus. */}
            <div onMouseEnter={show} onMouseLeave={hide}>
              <button
                className="hoverable"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 13,
                  fontWeight: 600,

                  textTransform: 'uppercase',
                  color: open || isSectors ? 'var(--blue)' : 'var(--ink)',
                  padding: '6px 10px',
                  transition: 'color 200ms ease',
                  fontFamily: 'inherit',
                  background: open || isSectors ? 'rgba(0,60,255,0.06)' : 'transparent',
                  borderRadius: 999,
                }}
              >
                Sectors
                <svg
                  width="10" height="6" viewBox="0 0 10 6" fill="none"
                  style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 250ms ease', flexShrink: 0 }}
                >
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <NavLink to="/projects" label="Projects" active={isProjects} onClick={() => handleSamePageClick('/projects')} />
            <NavLink to="/blog" label="Blogs" active={isBlog} onClick={() => handleSamePageClick('/blog')} />

            <Link
              to="/contact"
              className="hoverable"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                background: 'linear-gradient(135deg, var(--red), #cc0003)',
                color: 'white',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 700,

                boxShadow: '0 8px 24px rgba(255,0,4,0.35)',
                transition: 'transform 220ms ease, box-shadow 220ms ease',
                marginLeft: 8,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(255,0,4,0.48)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,0,4,0.35)';
              }}
            >
              Get In Touch
              <ArrowIcon />
            </Link>
          </>
        )}

        {/* Mobile: hamburger toggle */}
        {isMobile && (
          <button
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 999,
              background: mobileOpen ? 'rgba(0,60,255,0.08)' : 'transparent',
              border: '1px solid rgba(10,10,20,0.08)',
              cursor: 'pointer',
              transition: 'all 200ms ease',
              flexShrink: 0,
            }}
          >
            {mobileOpen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg width="18" height="13" viewBox="0 0 18 13" fill="none">
                <line x1="0" y1="1"  x2="18" y2="1"  stroke="var(--ink)" strokeWidth="2" strokeLinecap="round"/>
                <line x1="0" y1="6.5" x2="18" y2="6.5" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round"/>
                <line x1="0" y1="12" x2="14" y2="12" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        )}
      </nav>

      {/* Desktop mega dropdown — sectors as columns, each sector's own
          services nested underneath it, all routing to that sector's page. */}
      {!isMobile && open && (
        <div
          onMouseEnter={show}
          onMouseLeave={hide}
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'min(1040px, 98vw)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: -1, position: 'relative', zIndex: 1 }}>
            <svg width="24" height="10" viewBox="0 0 24 10" fill="none" style={{ display: 'block' }}>
              <path d="M12 2L22 10H2L12 2Z" fill="rgba(255,255,255,0.97)" stroke="rgba(10,10,20,0.08)" strokeWidth="1" strokeLinejoin="round" />
            </svg>
          </div>

          <div
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(28px)',
              WebkitBackdropFilter: 'blur(28px)',
              border: '1px solid rgba(10,10,20,0.08)',
              borderRadius: 20,
              boxShadow: '0 32px 80px rgba(10,10,20,0.13), 0 4px 16px rgba(10,10,20,0.05)',
              padding: '26px 30px 30px',
              animation: 'dropdownIn 220ms cubic-bezier(.2,.8,.2,1) forwards',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(10,10,20,0.4)', fontFamily: "'Inter', sans-serif" }}>
                Sectors &amp; Services
              </span>
              <Link
                to="/sectors"
                className="hoverable"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: 'var(--blue)', textDecoration: 'none' }}
              >
                View All Sectors <ArrowIcon size={10} />
              </Link>
            </div>

            <div className="sector-menu-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${SECTORS.length}, 1fr)`, gap: '0 18px' }}>
              {SECTORS.map(sector => <SectorColumn key={sector.name} sector={sector} />)}
            </div>
          </div>
        </div>
      )}

      {/* Mobile dropdown menu */}
      {isMobile && mobileOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            left: 0,
            right: 0,
            background: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(28px)',
            WebkitBackdropFilter: 'blur(28px)',
            border: '1px solid rgba(10,10,20,0.08)',
            borderRadius: 20,
            boxShadow: '0 32px 80px rgba(10,10,20,0.13)',
            padding: '12px 8px 16px',
            animation: 'dropdownIn 220ms cubic-bezier(.2,.8,.2,1) forwards',
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <MobileLink to="/" label="Home" active={isHome} onClick={() => handleSamePageClick('/')} />
            <MobileLink to="/about" label="About" active={isAbout} onClick={() => handleSamePageClick('/about')} />

            {/* Sectors accordion — expands into each sector, each sector into its own services */}
            <div>
              <button
                onClick={() => setMobileSectorsOpen(v => !v)}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 14px',
                  fontSize: 14, fontWeight: 600,
                  color: isSectors ? 'var(--red)' : 'var(--ink)',
                  background: isSectors ? 'rgba(255,0,4,0.05)' : 'transparent',
                  border: 'none', borderRadius: 12, fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                Sectors
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ transform: mobileSectorsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 220ms ease' }}>
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {mobileSectorsOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 6px 10px 14px' }}>
                  {SECTORS.map(sector => (
                    <div key={sector.name}>
                      <Link
                        to={sector.to}
                        onClick={closeMobile}
                        style={{
                          display: 'block', fontSize: 12.5, fontWeight: 700, letterSpacing: 0.3,
                          textTransform: 'uppercase', color: sector.color, padding: '4px 8px',
                        }}
                      >
                        {sector.name}
                      </Link>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2 }}>
                        {sector.services.map(svc => (
                          <Link
                            key={svc.slug}
                            to={`${sector.to}/${svc.slug}`}
                            onClick={closeMobile}
                            style={{ fontSize: 12.5, color: 'var(--ink-soft)', padding: '5px 8px', borderRadius: 8 }}
                          >
                            {svc.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <MobileLink to="/projects" label="Projects" active={isProjects} onClick={() => handleSamePageClick('/projects')} />
            <MobileLink to="/blog" label="Blogs" active={isBlog} onClick={() => handleSamePageClick('/blog')} />

            <div style={{ height: 1, background: 'rgba(10,10,20,0.07)', margin: '8px 16px' }} />

            <Link
              to="/contact"
              onClick={closeMobile}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                margin: '4px 8px 0',
                padding: '10px 18px',
                background: 'linear-gradient(135deg, var(--red), #cc0003)',
                color: 'white',
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 700,

                boxShadow: '0 8px 24px rgba(255,0,4,0.35)',
                justifyContent: 'center',
              }}
            >
              Get In Touch
              <ArrowIcon />
            </Link>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .sector-menu-grid { grid-template-columns: repeat(2, 1fr) !important; row-gap: 20px !important; }
        }
      `}</style>
    </div>
  );
}

function MobileLink({ to, href, label, active, onClick }) {
  const baseStyle = {
    display: 'block',
    padding: '9px 14px',
    fontSize: 14,
    fontWeight: 600,

    color: active ? 'var(--red)' : 'var(--ink)',
    borderRadius: 12,
    transition: 'background 200ms ease, color 200ms ease',
    background: active ? 'rgba(255,0,4,0.05)' : 'transparent',
  };
  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick}
        style={baseStyle}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(10,10,20,0.04)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        {label}
      </Link>
    );
  }
  return (
    <a
      href={href}
      onClick={onClick}
      style={baseStyle}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(10,10,20,0.04)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {label}
    </a>
  );
}

function NavLink({ to, href, label, active, onClick }) {
  const style = {
    fontSize: 13,
    fontWeight: 600,

    textTransform: 'uppercase',
    color: active ? 'var(--red)' : 'var(--ink)',
    position: 'relative',
    padding: '6px 10px',
    transition: 'color 200ms ease',
    borderRadius: 999,
    display: 'inline-block',
  };

  const underline = active ? (
    <span
      style={{
        position: 'absolute',
        left: 12, right: 12, bottom: 2,
        height: 2,
        background: 'var(--red)',
        borderRadius: 2,
        transformOrigin: 'left',
        animation: 'underlineGrow 500ms cubic-bezier(.2,.8,.2,1) forwards',
      }}
    />
  ) : null;

  if (to) {
    return (
      <Link to={to} className="hoverable" style={style} onClick={onClick}>
        {label}
        {underline}
      </Link>
    );
  }
  return (
    <a href={href} className="hoverable" style={style} onClick={onClick}>
      {label}
      {underline}
    </a>
  );
}

/* One sector column in the merged mega dropdown: a colored header linking to
   the sector page, followed by that sector's own services — every one of
   which also links to the same sector page, since services aren't sold as
   standalone pages outside their sector. */
function SectorColumn({ sector }) {
  const [hovHeader, setHovHeader] = useState(false);
  const rgb = hexToRgb(sector.color);
  return (
    <div>
      <Link
        to={sector.to}
        className="hoverable"
        onMouseEnter={() => setHovHeader(true)}
        onMouseLeave={() => setHovHeader(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: sector.color,
          padding: '6px 10px',
          background: hovHeader ? `rgba(${rgb},0.12)` : `rgba(${rgb},0.07)`,
          border: `1.5px solid rgba(${rgb},${hovHeader ? '0.35' : '0.18'})`,
          borderRadius: 8,
          marginBottom: 10,
          textDecoration: 'none',
          transition: 'background 160ms ease, border-color 160ms ease',
          letterSpacing: '0.02em',
        }}
      >
        <span style={{ flex: 1, lineHeight: 1.2 }}>{sector.name}</span>
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ opacity: hovHeader ? 1 : 0.55, transition: 'opacity 160ms, transform 160ms', transform: hovHeader ? 'translateX(2px)' : 'translateX(0)', flexShrink: 0 }}>
          <path d="M2 6h8M7 2.5l3.5 3.5L7 9.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Link>

      <div>
        {sector.services.map(svc => (
          <DropItem key={svc.slug} label={svc.title} to={`${sector.to}/${svc.slug}`} accent={sector.color} rgb={rgb} />
        ))}
      </div>
    </div>
  );
}

function DropItem({ label, to, accent, rgb }) {
  const [hov, setHov] = useState(false);
  const itemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '6px 8px',
    borderRadius: 9,
    fontSize: 12,
    lineHeight: 1.35,
    fontWeight: hov ? 600 : 450,
    color: hov ? accent : 'var(--ink-soft)',
    background: hov ? `rgba(${rgb},0.07)` : 'transparent',
    transition: 'all 130ms ease',

  };
  const dot = (
    <span
      style={{
        width: 5,
        height: 5,
        borderRadius: '50%',
        flexShrink: 0,
        marginTop: 5,
        background: hov ? accent : 'rgba(10,10,20,0.18)',
        transition: 'background 130ms ease, transform 130ms ease',
        transform: hov ? 'scale(1.4)' : 'scale(1)',
      }}
    />
  );
  return (
    <Link
      to={to}
      className="hoverable"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={itemStyle}
    >
      {dot}
      {label}
    </Link>
  );
}
